// React Query keys (invalidate all of them after any action).
export const PERF_KEYS = {
  mine: ["me:myEvaluations"],
  toEvaluate: (includeDone) => ["me:toEvaluate", includeDone],
  details: (id) => ["me:perfEvaluation", id],
};

// Prefixes of every performance query.
export const PERF_QUERY_PREFIXES = [
  "me:myEvaluations",
  "me:toEvaluate",
  "me:perfEvaluation",
];

export const invalidatePerformance = (queryClient) =>
  PERF_QUERY_PREFIXES.forEach((prefix) =>
    queryClient.invalidateQueries({ queryKey: [prefix] }),
  );

// How the employee sees each status. There is no sign-off: the result
// comes straight to the employee once the evaluator submits.
// PENDING_SIGN_OFF only exists on old rows (the server shows them as
// PENDING_ACKNOWLEDGEMENT); kept so nothing renders a raw code.
export const EMPLOYEE_STATUS = {
  PENDING_SELF: {
    label: "In progress: your self-assessment is due",
    tone: "amber",
    action: "Start self-assessment",
  },
  PENDING_EVALUATION: { label: "In progress", tone: "blue" },
  PENDING_SIGN_OFF: {
    label: "Waiting for your answer",
    tone: "amber",
    action: "Agree or contest",
  },
  PENDING_ACKNOWLEDGEMENT: {
    label: "Waiting for your answer",
    tone: "amber",
    action: "Agree or contest",
  },
  CONTESTED: { label: "Contested – waiting for HR", tone: "purple" },
  COMPLETED: { label: "Completed", tone: "green" },
  CANCELLED: { label: "Cancelled", tone: "gray" },
};

// How evaluators see each status.
export const REVIEWER_STATUS = {
  PENDING_SELF: {
    label: "Waiting for employee's self-assessment",
    tone: "gray",
  },
  PENDING_EVALUATION: { label: "To rate", tone: "amber" },
  PENDING_SIGN_OFF: { label: "With employee", tone: "blue" },
  PENDING_ACKNOWLEDGEMENT: { label: "With employee", tone: "blue" },
  CONTESTED: { label: "Contested by employee", tone: "purple" },
  COMPLETED: { label: "Completed", tone: "green" },
  CANCELLED: { label: "Cancelled", tone: "gray" },
};

export const ACTIVITY_LABELS = {
  CREATED: "Evaluation started",
  SELF_SUBMITTED: "Self-assessment submitted",
  SUBMITTED: "Submitted by evaluator",
  APPROVED: "Approved", // old sign-off flow
  RETURNED: "Returned to evaluator", // old sign-off flow
  ACKNOWLEDGED: "Acknowledged by employee",
  ACCEPTED: "Agreed by employee",
  CONTESTED: "Contested by employee",
  CONTEST_UPHELD: "HR upheld the result",
  CONTEST_REEVALUATE: "HR sent it back for re-evaluation",
  EVALUATOR_CHANGED: "Evaluator changed",
  CANCELLED: "Cancelled",
};

export const ACTION_STATUSES = [
  "PENDING_SELF",
  "PENDING_ACKNOWLEDGEMENT",
  "PENDING_SIGN_OFF",
];
export const OPEN_STATUSES = [
  "PENDING_SELF",
  "PENDING_EVALUATION",
  "PENDING_SIGN_OFF",
  "PENDING_ACKNOWLEDGEMENT",
  "CONTESTED",
];

// The employee's answer to the result.
export const EMPLOYEE_RESPONSE = {
  ACCEPT: { label: "Agreed", tone: "green", verb: "agree" },
  CONTEST: { label: "Contested", tone: "purple", verb: "contest" },
};

export const getPersonName = (person) =>
  person ? [person.firstName, person.lastName].filter(Boolean).join(" ") : "—";

export const isOverdue = (evaluation, todayKey) =>
  !!evaluation.dueDate &&
  evaluation.dueDate < todayKey &&
  OPEN_STATUSES.includes(evaluation.status);

// "4 – Exceeds Expectations", or just "4" when the scale has no label for it.
export const getScaleLabel = (scale, value) => {
  if (value == null) return "Not rated";
  const label = scale?.labels?.find((l) => l.value === value)?.label;
  return label ? `${value} – ${label}` : String(value);
};

export const getScaleValues = (scale) => {
  const values = [];
  for (let v = scale.min; v <= scale.max; v += 1) values.push(v);
  return values;
};

// An item's share of its section, as a % label.
export const getItemWeightLabel = (section, item) => {
  if (section.itemWeighting === "CUSTOM") return `${item.weight ?? 0}%`;
  const count = section.items?.length || 1;
  return `${Math.round((100 / count) * 10) / 10}%`;
};

// DOM ids used to scroll to a field from a validation message.
export const getFieldId = (sectionKey, itemKey) =>
  itemKey ? `perf-${sectionKey}-${itemKey}` : `perf-${sectionKey}`;

// Missing required answers, in the server's message format
// ("<section> / <item>: rating is required"), with the field to scroll to.
export const findMissingAnswers = (sections, responses) => {
  const missing = [];

  sections.forEach((section) => {
    const answer = responses?.[section.key] ?? {};

    if (section.type === "RATING") {
      (section.items ?? []).forEach((item) => {
        if (typeof answer.items?.[item.key]?.rating !== "number") {
          missing.push({
            message: `${section.title} / ${item.title}: rating is required`,
            fieldId: getFieldId(section.key, item.key),
          });
        }
      });
    } else if (section.required) {
      const isEmpty =
        (section.type === "TEXT" && !answer.text?.trim()) ||
        (section.type === "TEXT_LIST" &&
          !(answer.entries ?? []).some((e) => e?.trim())) ||
        (section.type === "TABLE" &&
          !(answer.rows ?? []).some((row) =>
            Object.values(row ?? {}).some((v) => String(v ?? "").trim()),
          ));
      if (isEmpty) {
        missing.push({
          message: `${section.title}: required`,
          fieldId: getFieldId(section.key),
        });
      }
    }
  });

  return missing;
};

// Maps server messages to fields by matching "<section title> / <item title>".
export const mapServerErrors = (sections, messages) =>
  messages.map((message) => {
    const section = sections.find((s) => message.startsWith(s.title));
    const item = section?.items?.find((i) =>
      message.startsWith(`${section.title} / ${i.title}:`),
    );
    return {
      message,
      fieldId: section ? getFieldId(section.key, item?.key) : null,
    };
  });

export const scrollToField = (fieldId) => {
  const element = fieldId && document.getElementById(fieldId);
  if (!element) return;
  element.scrollIntoView({ behavior: "smooth", block: "center" });
  element.classList.add("perf-flash");
  setTimeout(() => element.classList.remove("perf-flash"), 1600);
};

// The remarks of the latest RETURNED activity, when it came after the last submit.
export const getLatestReturn = (activities = []) => {
  const lastRelevant = [...activities]
    .reverse()
    .find((a) => a.action === "RETURNED" || a.action === "SUBMITTED");
  return lastRelevant?.action === "RETURNED" ? lastRelevant : null;
};

export const hasAnyAnswer = (responses) =>
  Object.keys(responses ?? {}).length > 0;
